import React from "react";
import { useSelector } from "react-redux";
import {
    formatNumberWithCommas,
    roundOff,
} from "repoV2/features/Common/utils/numbers";
import { IRepoV2ReduxState } from "repoV2/features/Common/modules/Reducer/Reducer.interface";
import useGetFormData from "repoV2/features/Common/modules/FormData/utils/useGetFormData";
import {
    DEFAULT_PAYMENT_CARD_TYPE,
    PAYMENT_CALCULATION_TYPE,
} from "repoV2/features/Payments/Payments.constants";
import { IPaymentDetails } from "repoV2/features/Common/modules/FormData/interfaces/PaymentDetails.interfaces";
import { getCurrencyLocaleValue } from "repoV2/features/Common/modules/Location/utils/getLocales";
import { FORM_KEYS } from "repoV2/features/Common/modules/FormData/FormData.constants";
import useOneTimeListingPrice from "repoV2/features/CheckoutPages/utils/useOneTimeListingPrice";
import useSetFormData from "repoV2/features/Common/modules/FormData/utils/useSetFormData";
import { useCheckoutBasicDetailsContext } from "../../../CheckoutBasicDetailsContextProvider";

export const usePartPaymentDetails = () => {
    const [showInstallments, setShowInstallments] = React.useState(false);

    const selectedCard =
        useGetFormData<IPaymentDetails["paymentCalculationType"]>({
            key: `${FORM_KEYS.payment_details}.${FORM_KEYS.payment_calculation_type}`,
        }) || DEFAULT_PAYMENT_CARD_TYPE;

    const { listingUuid } = useCheckoutBasicDetailsContext();
    const {
        basePrice,
        offerBasePrice,
        currency = "",
        currencyName,
        showStrikeOffPrice,
    } = useOneTimeListingPrice();

    const { setFormData } = useSetFormData();

    const partPaymentDetails =
        useSelector(
            (state: IRepoV2ReduxState) =>
                state.checkoutPages.listingDetails[listingUuid]
                    ?.partPaymentDetails?.data
        ) || {};

    const { installmentsList } = partPaymentDetails;
    const currencyLocale = getCurrencyLocaleValue(currencyName);

    const partPaymentCardDetails = {
        priceText: `${currency}${formatNumberWithCommas({
            num: roundOff(installmentsList[0]?.installment_price_final, 2),
            locale: currencyLocale,
        })}`,
    };

    const handleSelectedCardChange = (card: number) => {
        setFormData(
            `${FORM_KEYS.payment_details}.${FORM_KEYS.payment_calculation_type}`,
            card
        );
        if (card === PAYMENT_CALCULATION_TYPE.PART_PAYMENT) {
            setShowInstallments(true);
        }
    };

    return {
        installmentsList,
        defaultPriceDetails: {
            basePrice,
            offerBasePrice,
            currency,
            showStrikeOffPrice,
        },
        currencyLocale,
        partPaymentCardDetails,
        selectedCard,
        handleSelectedCardChange,
        showInstallments,
        setShowInstallments,
    };
};
